'use client';

import type { Dispatch } from 'react';
import { Check, Ban } from 'lucide-react';

import { cn } from '@/lib/utils';

import { SkyAtmosphereLayers } from '../components/SkyAtmosphereLayers';
import { IdentityCard } from '../components/IdentityCard';
import { StepShell } from './StepShell';
import {
  PUBLIC_PLINTH_PRESETS,
  PUBLIC_SKYBOX_PRESETS,
  type PlinthPreset,
  type SkyboxPreset,
} from '../data/presets';
import { isBrightSkybox } from '@/features/lobby/theming/registry';
import { VerifyXField } from '@/features/lobby/ui/VerifyXField';
import type { LaunchAction, LaunchDraft } from '../state/use-launch-wizard';

interface StepProps {
  draft: LaunchDraft;
  dispatch: Dispatch<LaunchAction>;
}

export function IdentityStep({ draft, dispatch }: StepProps) {
  const patch = (p: Partial<LaunchDraft>) => dispatch({ type: 'patch', patch: p });

  return (
    <StepShell title="Name your board">
      {/* Name + description + cover image live in a single drag-drop
          card so the creator doesn't have to hop between three
          separate fields to set the board's identity. The card itself
          is the drop target — `<IdentityCard>` plays the highlight
          state and runs the upload through `/api/ipfs/upload-image`. */}
      <IdentityCard
        name={draft.name}
        description={draft.description}
        imageUrl={draft.imageUrl}
        onNameChange={(name) => patch({ name })}
        onDescriptionChange={(description) => patch({ description })}
        onImageChange={(imageUrl) => patch({ imageUrl })}
      />

      <Field label="X (optional)" hint="Verify ownership">
        <VerifyXField
          value={draft.twitterHandle}
          onChange={(handle) => patch({ twitterHandle: handle })}
        />
      </Field>

      <Field label="Plinth" hint="What the board sits on">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 md:grid-cols-7">
          <NoneTile
            label="None"
            aspectClass="aspect-square"
            selected={draft.plinthId === null}
            onSelect={() => patch({ plinthId: null })}
          />
          {PUBLIC_PLINTH_PRESETS.map((plinth) => (
            <PlinthTile
              key={plinth.id}
              plinth={plinth}
              selected={draft.plinthId === plinth.id}
              onSelect={() => patch({ plinthId: plinth.id })}
            />
          ))}
        </div>
      </Field>

      <Field label="Sky" hint="Live preview">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 md:grid-cols-7">
          <NoneTile
            label="None"
            aspectClass="aspect-[4/3]"
            selected={draft.skyboxId === null}
            onSelect={() => patch({ skyboxId: null })}
          />
          {PUBLIC_SKYBOX_PRESETS.map((sky) => (
            <SkyTile
              key={sky.id}
              sky={sky}
              selected={draft.skyboxId === sky.id}
              onSelect={() => patch({ skyboxId: sky.id })}
            />
          ))}
        </div>
      </Field>
    </StepShell>
  );
}

// ---------------------------------------------------------------------------
// None tile — shared "no preset" picker. Matches the aspect of the picker
// row it sits in (square for plinth, 4/3 for sky) so the grid stays tidy.
// ---------------------------------------------------------------------------

function NoneTile({
  label,
  aspectClass,
  selected,
  onSelect,
  ariaLabel,
}: {
  label: string;
  aspectClass: string;
  selected: boolean;
  onSelect: () => void;
  /** Override for pickers where “none” means a defined default (e.g. sky). */
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={ariaLabel ?? `${label} (no preset)`}
      className={cn(
        'group relative overflow-hidden rounded-[4px] border transition-transform',
        aspectClass,
        selected
          ? 'border-amber-200/80 scale-[1.02]'
          : 'border-white/10 hover:border-white/25 hover:scale-[1.01]',
      )}
      style={{
        background:
          'repeating-linear-gradient(135deg, rgba(255,255,255,0.03) 0 6px, transparent 6px 12px), #080608',
      }}
    >
      <span className="absolute inset-0 grid place-items-center">
        <Ban
          className={cn(
            'h-5 w-5 transition',
            selected ? 'text-amber-200/90' : 'text-amber-100/40 group-hover:text-amber-100/70',
          )}
          strokeWidth={1.5}
        />
      </span>
      {selected && (
        <span className="absolute right-1 top-1 z-10 grid h-4 w-4 place-items-center rounded-full bg-black/70 text-amber-200">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 z-10 bg-black/55 px-1.5 py-0.5 text-center font-mono text-[8px] uppercase tracking-[0.18em] text-amber-100/80">
        {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Plinth tile — a 3/4 side-perspective "block" with the plinth material on top.
// ---------------------------------------------------------------------------

function PlinthTile({
  plinth,
  selected,
  onSelect,
}: {
  plinth: PlinthPreset;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Plinth · ${plinth.name}`}
      className={cn(
        'group relative aspect-square overflow-hidden rounded-[4px] border transition-transform',
        selected
          ? 'border-amber-200/80 scale-[1.02]'
          : 'border-white/10 hover:border-white/25 hover:scale-[1.01]',
      )}
      style={{ background: 'radial-gradient(ellipse at 50% 110%, rgba(255,200,120,0.18), transparent 60%), #05060a' }}
    >
      {/* Plinth body — isometric block */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 h-[58%] w-[76%] -translate-x-1/2 -translate-y-[42%] rounded-[3px]"
        style={{
          background: plinth.background,
          boxShadow: `0 8px 14px -6px rgba(0,0,0,0.6), inset 0 -8px 10px -8px ${plinth.edge}99, inset 0 0 0 1px ${plinth.edge}55`,
        }}
      >
        {plinth.glyph && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center font-mono text-xl text-white/30 mix-blend-overlay"
          >
            {plinth.glyph}
          </span>
        )}
      </div>

      {/* Thin grid hint sitting on top of the plinth */}
      <div
        aria-hidden
        className="absolute left-1/2 top-[32%] h-1 w-[54%] -translate-x-1/2 rounded-full opacity-70"
        style={{
          background: `linear-gradient(90deg, transparent, ${plinth.edge}aa, transparent)`,
        }}
      />

      {selected && (
        <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-black/70 text-amber-200">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-0.5 text-center font-mono text-[8px] uppercase tracking-[0.18em] text-amber-100/80">
        {plinth.name}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sky tile — live, animated mini skybox (drifting nebula + twinkling stars).
// ---------------------------------------------------------------------------

function SkyTile({
  sky,
  selected,
  onSelect,
}: {
  sky: SkyboxPreset;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Sky · ${sky.name}`}
      className={cn(
        'group relative aspect-[4/3] overflow-hidden rounded-[4px] border transition-transform',
        selected
          ? 'border-amber-200/80 scale-[1.02]'
          : 'border-white/10 hover:border-white/25 hover:scale-[1.01]',
      )}
      style={{ background: sky.background }}
    >
      <SkyAtmosphereLayers sky={sky} variant="tile" />

      {selected && (
        <span className="absolute right-1 top-1 z-10 grid h-4 w-4 place-items-center rounded-full bg-black/70 text-amber-200">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
      <span
        className={cn(
          'absolute inset-x-0 bottom-0 z-10 px-1.5 py-0.5 text-center font-mono text-[8px] uppercase tracking-[0.18em]',
          isBrightSkybox(sky.id)
            ? 'bg-white/85 text-zinc-900'
            : 'bg-black/55 text-amber-100/80',
        )}
      >
        {sky.name}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Local primitives
// ---------------------------------------------------------------------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-amber-400/15 bg-gradient-to-b from-zinc-950/70 to-black/60 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/70">
          {label}
        </span>
        {hint && (
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-100/40">
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
